// src/ui/UndoToast.jsx
//
// 5-second undo banner surfaced after a swipe (or keyboard equivalent) commits
// a mark-done or clear action on a Care/Pottering task row. Clicking Undo
// reverts the action; auto-dismisses after the timer.

import React, { useEffect } from 'react';
import { RotateCcw } from 'lucide-react';

const DEFAULT_MS = 5000;

export default function UndoToast({ label, onUndo, onDismiss, durationMs = DEFAULT_MS }) {
  useEffect(() => {
    if (!onDismiss) return undefined;
    const t = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(t);
  }, [onDismiss, durationMs, label]);

  return (
    <div
      role="status"
      aria-live="polite"
      data-undo-toast="true"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: `calc(16px + env(safe-area-inset-bottom, 0px))`,
        margin: '0 auto',
        maxWidth: 420,
        background: 'var(--c-bg)',
        borderLeft: '3px solid var(--c-heading)',
        borderRadius: 'var(--radius-md, 12px)',
        boxShadow: 'var(--shadow-md, 0 4px 16px rgba(0,0,0,0.14))',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        zIndex: 3100,
        animation: 'tendUndoIn 220ms cubic-bezier(0.4,0,0.2,1)',
        fontFamily: 'Lora, serif',
      }}
    >
      <span style={{ flex: 1, fontSize: 'var(--font-body)', color: 'var(--c-text)', lineHeight: 1.45 }}>
        {label}
      </span>
      <button
        type="button"
        onClick={() => { onUndo?.(); onDismiss?.(); }}
        aria-label="Undo last action"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'var(--c-heading)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--radius-pill, 999px)',
          padding: '8px 14px',
          fontSize: 'var(--font-small)',
          fontWeight: 700,
          cursor: 'pointer',
          minHeight: 36,
        }}
      >
        <RotateCcw size={14} aria-hidden="true"/> Undo
      </button>
      <style>{`
        @keyframes tendUndoIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-undo-toast="true"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
